import * as React from 'react';

import AlertActionError from '../alerts/AlertActionError';

import AddInvestmentAccountsFlow from './AddInvestmentAccountsFlow';

/**
 * One message for every failure: the action is only offered where the server
 * said it could succeed, so a refusal means that answer went stale, and to the
 * user every code here reads the same — it did not work, try again.
 */
const ADD_INVESTMENT_ACCOUNTS_ERROR_MESSAGE = 'We could not add investment accounts from that bank. Please try again.';

/** The slice of an account the action's eligibility is read from. */
interface IAddInvestmentsCandidate {
  bankConnection?: { needsReconnection: boolean; canAddInvestments: boolean } | null;
}

/**
 * Whether to offer "Add investment accounts" on an account. A connection that
 * needs reconnecting leads with its fix instead: both are update-mode sessions
 * on the same Plaid Item.
 */
export const canOfferAddInvestments = (account: IAddInvestmentsCandidate): boolean =>
  !!account.bankConnection?.canAddInvestments && !account.bankConnection.needsReconnection;

/** The menu label for the action, naming the bank the accounts come from. */
export const addInvestmentAccountsLabel = (bankName: string): string => `Add investment accounts from ${bankName}`;

export interface IAddInvestmentAccounts {
  /** Starts a session against the connection; a second call replaces the first. */
  addInvestmentAccounts: (bankConnectionId: string) => void;
  /**
   * Render inside the surface offering the action. Carries the in-flight
   * session and any error it produced; nothing is visible unless one failed.
   */
  addInvestmentAccountsSlot: React.ReactNode;
}

/**
 * Runs the add-investment-accounts session for a surface that lists accounts,
 * mounting a single {@link AddInvestmentAccountsFlow} keyed to the connection
 * rather than one Plaid state machine per row.
 *
 * @param errorClassName Layout classes for the failure line.
 * @returns `addInvestmentAccounts`, to call from the menu item, and
 *   `addInvestmentAccountsSlot`, which the surface has to render for a session
 *   to run at all.
 */
export function useAddInvestmentAccounts(errorClassName?: string): IAddInvestmentAccounts {
  const [bankConnectionId, setBankConnectionId] = React.useState<string | null>(null);
  const [hasError, setHasError] = React.useState(false);

  const addInvestmentAccounts = React.useCallback((connectionId: string) => {
    setHasError(false);
    setBankConnectionId(connectionId);
  }, []);

  const handleDone = React.useCallback(() => setBankConnectionId(null), []);

  const handleError = React.useCallback(() => {
    setHasError(true);
    // Unmounts the flow, so choosing the action again mounts a fresh one.
    setBankConnectionId(null);
  }, []);

  const addInvestmentAccountsSlot = (
    <>
      {bankConnectionId && (
        <AddInvestmentAccountsFlow
          key={bankConnectionId}
          bankConnectionId={bankConnectionId}
          onDone={handleDone}
          onError={handleError}
        />
      )}
      {hasError && <AlertActionError message={ADD_INVESTMENT_ACCOUNTS_ERROR_MESSAGE} className={errorClassName} />}
    </>
  );

  return { addInvestmentAccounts, addInvestmentAccountsSlot };
}
